import { SupabaseClient } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { useInfiniteQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useCallback, useEffect, useRef } from 'react';

import { GetPrototypesResponse } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';
import { Database } from 'types/supabase';

interface IGetPrototypesQueryFnParams {
  supabase: SupabaseClient<Database>;
  userId?: string | null;
  limit?: number | null;
  pageParam?: any | null;
}

export const getPrototypesQueryFn = async ({
  supabase,
  userId,
  limit = 6,
  pageParam,
}: IGetPrototypesQueryFnParams) => {
  const prototypesQuery = supabase
    .from('user_code_view')
    .select('*')
    .eq('type', 'prototype')
    .not('user_id', 'is', null)
    .limit(limit || 6);

  if (userId) {
    prototypesQuery.eq('user_id', userId).order('completed_date', { ascending: false });
  } else {
    prototypesQuery
      .or(`html.neq.,css.neq.,js.neq.`)
      .eq('is_private', false)
      .order('total_score', { ascending: false })
      .order('completed_date', { ascending: false });
  }

  if (pageParam) {
    if (userId) {
      prototypesQuery.lt('completed_date', pageParam?.completed_date);
    } else {
      prototypesQuery
        .lte('total_score', pageParam?.total_score)
        .lt('completed_date', pageParam?.completed_date);
    }
  }

  const prototypes = await prototypesQuery;

  if (prototypes.error) throw prototypes.error;

  return prototypes.data;
};

interface IUseGetPrototypesQueryResponse {
  data?: GetPrototypesResponse | null;
  isFetching: boolean;
  refetch: any;
  ref: any;
}

interface IUseGetPrototypesQueryParams {
  userId?: string | null;
}

export const useGetPrototypesQuery = ({
  userId,
}: IUseGetPrototypesQueryParams): IUseGetPrototypesQueryResponse => {
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();

  const { data, fetchNextPage, hasNextPage, refetch, isFetching } =
    useInfiniteQueryWithErrorBoundary({
      gcTime: 0,
      queryKey: [TanstackQueryName.GetPrototypes, userId],
      queryFn: ({ pageParam }) => {
        return getPrototypesQueryFn({ supabase, userId, limit: 6, pageParam });
      },
      placeholderData: {
        pageParams: [],
        pages: [],
      },
      initialPageParam: null,
      getNextPageParam: (lastPage) => {
        if (lastPage && Array.isArray(lastPage) && lastPage?.length > 0) {
          const lastPageParams = lastPage[lastPage.length - 1];

          return lastPageParams;
        }

        return undefined;
      },
    });

  const observer = useRef<IntersectionObserver>();
  const lastPrototypeRef = useCallback(
    (node) => {
      if (isFetching) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        },
        {
          rootMargin: '100px',
        }
      );

      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetching]
  );

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: [TanstackQueryName.GetPrototypes, userId],
      });
    };
  }, []);

  return {
    data,
    isFetching,
    refetch,
    ref: lastPrototypeRef,
  };
};
